import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Link,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as yup from "yup";

import { Hero2Text } from "components/shared/Typography";
import FormikFormField from "components/shared/FormikFormField";
import PasswordToggleIcon from "components/shared/PasswordToggleIcon";
import { CHANGE_PASSWORD } from "helpers/apis";
import { ToastTypes } from "hooks/useApp";
import useAxios from "helpers/axios";
import { IForm, passwordValidation } from "types/form";

interface IPasswordChangeForm extends IForm {
  oldPassword: string;
  newPassword: string;
}

const validationSchema = yup.object({
  oldPassword: passwordValidation,
  newPassword: passwordValidation,
});

const requirements = [
  "Must contain at least 9 characters.",
  "Must contain 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character  from the following: @$!%*#?&",
];

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: "100%",
  },
  title: {
    letterSpacing: 0.1,
    fontWeight: 400,
  },
  requirements: {
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: 0.1,
    fontFamily: "'Futura Allele', sans-serif",
    paddingInlineStart: 23,
    margin: "16px 0",
  },
  linkP2: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
    letterSpacing: theme.typography.body2.letterSpacing,
    "&:hover": { textDecoration: "underline" },
  },
}));

export const PasswordChange = ({ userId }: { userId?: number }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [oldPasswordVisible, setOldPasswordVisible] = useState<boolean>(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState<boolean>(false);
  const classes = useStyles();

  const { fetchData, loading } = useAxios({
    config: { url: CHANGE_PASSWORD, method: "POST" },
    callOnInit: false,
  });

  const onSubmit = (
    values: IPasswordChangeForm,
    actions: FormikHelpers<IPasswordChangeForm>
  ) => {
    fetchData(
      {
        data: {
          password: values.oldPassword,
          newPassword: values.newPassword,
        },
      },
      ({
        response,
        setToastMessage,
      }: {
        response: {
          data: null;
          message: string;
        };
        setToastMessage: Function;
      }) => {
        setToastMessage({
          message: response.message,
          type: ToastTypes.SUCCESS,
        });
        actions.resetForm();
      }
    );
  };

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
    >
      <AccordionSummary>
        <Grid container justifyContent="space-between" alignItems="center">
          <Hero2Text>Change Password</Hero2Text>
          <Box fontWeight={600}>
            <Link color="secondary">{expanded ? "Cancel" : "Edit"}</Link>
          </Box>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Formik
          initialValues={{
            oldPassword: "",
            newPassword: "",
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {(props: FormikProps<IPasswordChangeForm>) => {
            const { dirty, isValid } = props;

            return (
              <Form className={classes.form}>
                <Grid container direction="column">
                  <Typography
                    component="p"
                    variant="subtitle2"
                    className={classes.title}
                  >
                    Requirements for creating a new and strong password
                  </Typography>
                  <ul className={classes.requirements}>
                    {requirements.map((requirement: string, index: number) => (
                      <li key={index}>{requirement}</li>
                    ))}
                  </ul>
                  <Box width={1} mt={1} mb={3}>
                    <FormikFormField<IPasswordChangeForm>
                      name="oldPassword"
                      type={oldPasswordVisible ? "text" : "password"}
                      {...props}
                      textFieldProps={{
                        InputProps: {
                          endAdornment: (
                            <PasswordToggleIcon
                              visible={oldPasswordVisible}
                              setVisible={setOldPasswordVisible}
                            />
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Box width={1} mb={4}>
                    <FormikFormField<IPasswordChangeForm>
                      name="newPassword"
                      type={newPasswordVisible ? "text" : "password"}
                      {...props}
                      textFieldProps={{
                        InputProps: {
                          endAdornment: (
                            <PasswordToggleIcon
                              visible={newPasswordVisible}
                              setVisible={setNewPasswordVisible}
                            />
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      // Do not allow updating a password for other user
                      disabled={loading || !dirty || !isValid || !!userId}
                      disableElevation
                    >
                      Update Password
                    </Button>
                  </Box>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};
